import React, { useState } from 'react';
import styled from 'styled-components';
import { I } from '../../../../common/v5/config';
import { V5 } from '../../../../common/path';
import WorkflowToolbar from './WorkflowToolbar';

const HeaderContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  align-items: center;
  background-color: var(--cention-color-background-xlight);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-foreground-base);
  min-height: var(--cention-header-height);
  z-index: 100;
`;

const MainHeader = styled.div`
  min-height: var(--cention-header-height);
  background-color: var(--cention-color-background-xlight);
  width: 100%;
  box-sizing: border-box;
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-foreground-base);
`;

const TopMenu = styled.div`
  position: relative;
  display: flex;
  height: var(--cention-header-height);
  align-items: center;
  font-size: 0.9em;
  font-weight: var(--cention-font-weight-regular);
  overflow-x: auto;
  overflow-y: hidden;
`;

const WorkflowDetailsSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
  min-width: 0;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  background-color: var(--cention-color-background-xlight);
`;

const NavigationSection = styled.div`
  display: flex;
  align-items: center;
  padding: 0 var(--cention-spacing-m);
  height: 100%;
  border-right: var(--cention-border-width-base) solid var(--cention-color-foreground-base);
  gap: var(--cention-spacing-s);
  background: linear-gradient(135deg, rgba(255, 109, 90, 0.05) 0%, rgba(255, 87, 34, 0.02) 100%);
`;

const ReturnButton = styled.a`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  color: var(--cention-color-text-base);
  text-decoration: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: var(--cention-font-weight-medium);
  transition: all 200ms ease-in-out;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
    border-color: rgba(0, 0, 0, 0.12);
    color: var(--cention-color-primary);
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  
  i {
    margin-right: 6px;
    font-size: 11px;
    opacity: 0.7;
  }
  
  &:hover i {
    opacity: 1;
  }
`;

const BreadcrumbSeparator = styled.div`
  color: var(--cention-color-text-lighter);
  font-size: 14px;
  opacity: 0.4;
  font-weight: 300;
  margin: 0 2px;
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  padding: 0 var(--cention-spacing-m);
  height: 100%;
  border-right: var(--cention-border-width-base) solid var(--cention-color-foreground-base);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--cention-color-text-dark);
  
  .logo-icon {
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #ff6d5a 0%, #ff5722 100%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--cention-spacing-xs);
    color: white;
    font-size: 12px;
    font-weight: 700;
  }
`;

const WorkflowInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-m);
  padding: 0 var(--cention-spacing-m);
  height: 100%;
  min-width: 0;
`;

const WorkflowNameSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  min-width: 0;
`;

const WorkflowName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--cention-color-text-dark);
  cursor: pointer;
  padding: var(--cention-spacing-2xs) var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  border: 1px solid transparent;
  min-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background-color: var(--cention-color-background-light);
    border-color: var(--cention-color-border-base);
  }
`;

const WorkflowNameInput = styled.input`
  font-size: 16px;
  font-weight: 600;
  color: var(--cention-color-text-dark);
  background: white;
  border: 1px solid var(--cention-color-primary);
  border-radius: var(--cention-border-radius-small);
  padding: var(--cention-spacing-2xs) var(--cention-spacing-xs);
  min-width: 120px;
  outline: none;
  box-shadow: 0 0 0 2px var(--cention-color-primary-tint-2);
`;

const WorkflowStatus = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-2xs);
  font-size: 12px;
  color: var(--cention-color-text-light);
  
  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.active ? 'var(--cention-color-success)' : 'var(--cention-color-text-lighter)'};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  padding-right: var(--cention-spacing-m);
`;

const ThemeToggle = styled.button`
  background: none;
  border: none;
  padding: var(--cention-spacing-xs);
  border-radius: var(--cention-border-radius-small);
  cursor: pointer;
  color: var(--cention-color-text-base);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--cention-color-background-light);
  }
  
  i {
    font-size: 16px;
  }
`;

const TabBarContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
  min-height: 30px;
  display: flex;
  padding: var(--cention-spacing-5xs);
  background-color: var(--cention-color-foreground-base);
  border-radius: var(--cention-border-radius-base);
  transition: all 150ms ease-in-out;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TabButton = styled.button`
  background: ${props => props.active ? 'var(--cention-color-background-xlight)' : 'transparent'};
  border: none;
  padding: var(--cention-spacing-xs) var(--cention-spacing-s);
  border-radius: var(--cention-border-radius-small);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.active ? 'var(--cention-color-text-dark)' : 'var(--cention-color-text-base)'};
  transition: all 150ms ease-in-out;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--cention-color-background-light);
    color: var(--cention-color-text-dark);
  }
`;

const CentionHeader = ({ 
  workflowName = 'My workflow',
  workflowActive = true,
  onWorkflowNameChange,
  onThemeToggle,
  currentTheme = 'light',
  activeTab = 'editor',
  onTabChange,
  workflows = [],
  onWorkflowChange,
  onNodesChange,
  onConnectionsChange,
  areaList = [],
  currentWorkflow
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(workflowName);

  const handleNameClick = () => {
    setIsEditingName(true);
    setTempName(workflowName);
  };

  const handleNameSubmit = () => {
    if (tempName.trim()) {
      onWorkflowNameChange?.(tempName.trim());
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setTempName(workflowName);
    }
  };

  const tabs = [
    { id: 'editor', label: I('Editor'), icon: 'fa fa-code' },
    { id: 'settings', label: I('Settings'), icon: 'fa fa-cog' }
  ];

  return (
    <HeaderContainer>
      <MainHeader>
        <TopMenu>
          <LogoSection>
            <NavigationSection>
            <Logo>
                <div className="logo-icon">🤖</div>
              Agent Builder
            </Logo>
            </NavigationSection>
          </LogoSection>
          
          <WorkflowDetailsSection>
            <WorkflowInfo>
              <WorkflowNameSection>
                {isEditingName ? (
                  <WorkflowNameInput
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onBlur={handleNameSubmit}
                    onKeyDown={handleNameKeyDown}
                    autoFocus
                  />
                ) : (
                  <WorkflowName onClick={handleNameClick} title={I('Click to edit workflow name')}>
                    {workflowName}
                  </WorkflowName>
                )}
                <WorkflowStatus active={workflowActive}>
                  <div className="status-indicator" />
                  {workflowActive ? I('Active') : I('Inactive')}
                </WorkflowStatus>
              </WorkflowNameSection>
            </WorkflowInfo>
          </WorkflowDetailsSection>

          <HeaderActions>
            {/* Include WorkflowToolbar for Save/Load functionality - moved to right next to theme toggle */}
            <WorkflowToolbar
              workflows={workflows}
              onWorkflowChange={onWorkflowChange}
              onNodesChange={onNodesChange}
              onConnectionsChange={onConnectionsChange}
              areaList={areaList}
              currentWorkflow={currentWorkflow}
            />
            <ThemeToggle onClick={onThemeToggle} title={I('Toggle theme')}>
              <i className={currentTheme === 'light' ? 'fa fa-moon-o' : 'fa fa-sun-o'} />
            </ThemeToggle>
          </HeaderActions>
        </TopMenu>

        <TabBarContainer>
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => onTabChange?.(tab.id)}
            >
              <i className={tab.icon} style={{ marginRight: '6px' }} />
              {tab.label}
            </TabButton>
          ))}
        </TabBarContainer>
      </MainHeader>
    </HeaderContainer>
  );
};

export default CentionHeader;
          